import React, { memo } from "react"
import {
  Search,
  SearchOff,
  FilterList,
  Close,
} from "@mui/icons-material"
import { Box, IconButton, Tooltip, Popover, Checkbox } from "@material-ui/core"
import {
  filterButtonSx,
  filterSectionTitleSx,
  searchInputSx,
  searchInputWrapperSx,
  searchToolbarStickySx,
  themePalette,
  webkitScrollbar,
  webkitscrollbarTrack,
} from "./styles"

// Toolbar y popover de filtros del panel izquierdo.
// Vive aparte para que LeftPanel quede enfocado en la lista y su virtualizacion.
function LeftPanelFilters({
  isMobile,
  searchTerm,
  setSearchTerm,
  activeFilterCount,
  handleOpenFilters,
  filtersAnchorEl,
  handleCloseFilters,
  filterPopoverPaperStyle,
  filterSections,
  selectedFilters,
  toggleFilterValue,
  clearAllFilters,
}) {
  return (
    <Box sx={searchToolbarStickySx}>
      <Box sx={searchInputWrapperSx}>
        {searchTerm ? (
          <IconButton
            size="small"
            onClick={() => setSearchTerm("")}
            style={{ padding: 4, color: themePalette.primary.main }}
          >
            <SearchOff fontSize="small" />
          </IconButton>
        ) : (
          <Search fontSize="small" style={{ color: themePalette.primary.main, flexShrink: 0 }} />
        )}
        <input
          type="text"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Buscar por nombre o mac"
          style={searchInputSx}
        />
        <Tooltip title={activeFilterCount ? `${activeFilterCount} filtros activos` : "Filtros"}>
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <IconButton onClick={handleOpenFilters} sx={{ ...filterButtonSx }}>
              <FilterList fontSize="small" />
            </IconButton>
            {activeFilterCount > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: "-0.28rem",
                  right: "-0.2rem",
                  minWidth: "1.05rem",
                  height: "1.05rem",
                  borderRadius: "9999px",
                  backgroundColor: themePalette.primary.main,
                  color: themePalette.common.white,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 0.2rem",
                  boxShadow: themePalette.shadow.accent,
                }}
              >
                {activeFilterCount}
              </span>
            )}
          </Box>
        </Tooltip>
      </Box>

      <Popover
        open={Boolean(filtersAnchorEl)}
        anchorEl={filtersAnchorEl}
        onClose={handleCloseFilters}
        style={{ zIndex: 2000 }}
        anchorReference={isMobile ? "anchorPosition" : "anchorEl"}
        anchorPosition={isMobile ? { top: 0, left: 0 } : undefined}
        anchorOrigin={isMobile ? undefined : { vertical: "bottom", horizontal: "right" }}
        transformOrigin={isMobile ? { vertical: "top", horizontal: "left" } : { vertical: "top", horizontal: "right" }}
        PaperProps={{
          style: filterPopoverPaperStyle,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            justifyContent: "space-between",
            rowGap: isMobile ? "0.35rem" : 0,
            padding: isMobile ? "0.95rem 1rem" : "0.8rem 0.95rem",
            borderBottom: `1px solid ${themePalette.border.subtle}`,
            backgroundColor: themePalette.surface.popover,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", minWidth: 0 }}>
            {isMobile && (
              <IconButton
                size="small"
                onClick={handleCloseFilters}
                style={{ padding: 4, color: themePalette.primary.main }}
              >
                <Close fontSize="small" />
              </IconButton>
            )}
            <span style={{ fontSize: "0.9rem", fontWeight: 700, color: themePalette.text.title, whiteSpace: "nowrap" }}>Filtros</span>
          </Box>
          <button
            type="button"
            onClick={clearAllFilters}
            style={{
              border: "none",
              background: "transparent",
              color: themePalette.primary.main,
              fontSize: isMobile ? "0.74rem" : "0.8rem",
              fontWeight: 600,
              cursor: "pointer",
              padding: isMobile ? "0 0.4rem 0 0" : 0,
              alignSelf: isMobile ? "flex-end" : "auto",
              whiteSpace: "nowrap",
            }}
          >
            {isMobile ? "Desactivar" : "Desactivar filtros"}
          </button>
        </Box>

        <Box
          sx={{
            flex: isMobile ? 1 : undefined,
            minHeight: 0,
            maxHeight: isMobile ? "none" : "560px",
            overflowY: "auto",
            padding: isMobile ? "0.25rem 0" : "0.2rem 0",
            "&::-webkit-scrollbar": { ...webkitScrollbar, width: "7px" },
            "&::-webkit-scrollbar-track": { ...webkitscrollbarTrack, background: themePalette.status.scrollbarTrackLight },
            "&::-webkit-scrollbar-thumb": { background: themePalette.status.scrollbarThumb },
          }}
        >
          {filterSections.map((section) => (
            <Box
              key={section.key}
              sx={{
                padding: isMobile ? "0.75rem 1rem" : "0.62rem 0.9rem",
                borderBottom: `1px solid ${themePalette.border.section}`,
                boxShadow: `inset 0 -1px 0 ${themePalette.border.inset}`,
              }}
            >
              <div style={filterSectionTitleSx}>{section.title}</div>
              {section.options.map((option) => {
                const isChecked = selectedFilters[section.key].includes(option.value)

                return (
                  <Box
                    key={option.value}
                    onClick={() => toggleFilterValue(section.key, option.value)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "0.55rem",
                      minHeight: isMobile ? "34px" : "30px",
                      padding: isMobile ? "0.08rem 0.2rem" : "0.02rem 0.2rem",
                      borderRadius: "0.45rem",
                      cursor: "pointer",
                      transition: "background-color 120ms ease",
                      "&:hover": {
                        backgroundColor: themePalette.surface.panel,
                      },
                    }}
                  >
                    <span style={{ fontSize: isMobile ? "0.92rem" : "0.88rem", color: themePalette.text.hint }}>{option.label}</span>
                    <Checkbox
                      checked={isChecked}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => toggleFilterValue(section.key, option.value)}
                      color="primary"
                      size="small"
                      style={{ padding: 2 }}
                    />
                  </Box>
                )
              })}
            </Box>
          ))}
        </Box>
      </Popover>
    </Box>
  )
}

export default memo(LeftPanelFilters)
